"use client";

import Image from "next/image";
import { Mail, MessageSquare, Phone, Globe } from "lucide-react";
import { TiktokIcon, FacebookIcon, InstagramIcon } from "@/components/shared/svg/Icons";
import { ContactInfoCard, QuoteRequestCard } from "@/components/client/contact/components/ContactComponents";

import { useSettings } from "@/features/settings/hook/useSettings";

export default function ContactPage() {
    const { data: settings } = useSettings();

    const socialLinks = {
        facebook: settings?.social?.facebook ? `https://facebook.com/${settings.social.facebook}` : "#",
        instagram: settings?.social?.instagram ? `https://instagram.com/${settings.social.instagram.replace("@", "")}` : "#",
        tiktok: settings?.social?.tiktok ? `https://tiktok.com/@${settings.social.tiktok.replace("@", "")}` : "#",
        whatsapp: settings?.social?.whatsapp ? `https://wa.me/${settings.social.whatsapp.replace(/\D/g, "")}` : "#",
    };

    const mainLocation = settings?.locations?.find(loc => loc.isMain) || settings?.locations?.[0];
    const mainPhone = mainLocation?.phone || "+213 27 31 16 23";
    const mainEmail = mainLocation?.email || "Commercial@Zahraoui.com";

    return (
        <main className="min-h-screen bg-[#FBF9F4] pb-20">
            {/* Header Section */}
            <section className="container mx-auto px-4 pt-16 mb-20">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div className="max-w-2xl order-2 lg:order-1">
                        <h1 className="font-brastika text-5xl md:text-7xl text-primary-900 mb-8 leading-tight">
                            Contactez la Conciergerie Zehraoui
                        </h1>
                        <p className="text-primary-700 text-lg md:text-xl leading-relaxed opacity-80">
                            Notre conciergerie est à votre disposition pour vous guider dans l&apos;univers des saveurs algériennes.
                            Que ce soit pour une commande spéciale ou un conseil sur nos assemblages, nous sommes à votre écoute.
                        </p>
                    </div>

                    <div className="relative order-1 lg:order-2 h-[400px] md:h-[600px] rounded-2xl overflow-hidden shadow-xl">
                        <Image
                            src="/images/site/contact_header.png"
                            alt="L'Art de l'Épice"
                            fill
                            className="object-cover"
                        />
                        <div className="absolute bottom-10 -left-6 bg-[#FFD1A5] px-8 py-4 rounded-lg transform -rotate-2 shadow-lg">
                            <span className="font-brastika text-xl text-primary-900">L&apos;Art de l&apos;Épice</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Methods Grid */}
            <section className="container mx-auto px-4 mb-20">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {/* Left Column: Email & Phone */}
                    <div className="space-y-8 md:col-span-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <ContactInfoCard
                                title="Écrivez-nous"
                                value={mainEmail}
                                icon={Mail}
                                href={`mailto:${mainEmail}`}
                            />
                            <ContactInfoCard
                                title="WhatsApp Business"
                                value={settings?.social?.whatsapp || "+213 XX XX XX XX XX"}
                                icon={MessageSquare}
                                href={socialLinks.whatsapp}
                            />
                            <ContactInfoCard
                                title="Appel Direct"
                                value={mainPhone}
                                icon={Phone}
                                href={`tel:${mainPhone}`}
                            />
                            <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-50">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600">
                                        <Globe className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-brastika text-xl text-primary-900">Réseaux Sociaux</h3>
                                </div>
                                <div className="flex flex-wrap gap-4 pt-2">
                                    <span className="text-[10px] font-bold tracking-widest uppercase text-primary-400">INSTAGRAM</span>
                                    <span className="text-[10px] font-bold tracking-widest uppercase text-primary-400">FACEBOOK</span>
                                    <span className="text-[10px] font-bold tracking-widest uppercase text-primary-400">TIKTOK</span>
                                </div>
                                <div className="flex gap-11 mt-4">
                                    <a href={socialLinks.instagram} className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white hover:bg-primary-900 transition-colors"><InstagramIcon className="w-5 h-5" /></a>
                                    <a href={socialLinks.facebook} className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white hover:bg-primary-900 transition-colors"><FacebookIcon className="w-5 h-5" /></a>
                                    <a href={socialLinks.tiktok} className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white hover:bg-primary-900 transition-colors"><TiktokIcon className="w-5 h-5" /></a>
                                </div>
                            </div>


                        </div>
                    </div>

                    {/* Right Column: Quote Request */}
                    <div className="lg:col-span-1">
                        <QuoteRequestCard />
                    </div>
                </div>
            </section>

            {/* Bottom Banner */}
            <section className="container mx-auto px-4">
                <div className="bg-[#F8F3ED] rounded-3xl p-12 md:p-20 flex flex-col md:flex-row items-center gap-12 border border-primary-100/50">
                    <div className="flex-grow max-w-3xl">
                        <h2 className="font-brastika text-4xl md:text-5xl text-primary-950 mb-8">Un héritage préservé</h2>
                        <p className="text-primary-700/80 text-lg leading-relaxed">
                            Chaque grain, chaque écorce, chaque pétale que nous sélectionnons raconte une histoire vieille de plusieurs siècles.
                            Zehraoui s&apos;engage à préserver les méthodes artisanales de transformation pour garantir une pureté inégalée.
                        </p>
                    </div>
                    <div className="shrink-0 w-48 h-48 relative md:ml-auto">
                        <Image
                            src={settings?.logoUrl || "/assets/logo/logo.png"}
                            alt="Logo Zehraoui"
                            fill
                            className="object-contain"
                        />
                    </div>
                </div>
            </section>
        </main>
    );
}
